import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';

export default function Flyttegods() {
  return (
    <>
      <SEO
        title="Flyttegods Generelt | Fastlane ApS"
        description="Vi koordinerer flyttegods på paller eller i containere i hele verden. Rådgivning om pakkelister, CSC-godkendelse, fragtforsikring og afhentning."
        canonicalUrl="https://fastlane.dk/flyttegods"
      />

      <PageHeader
        title="Flyttegods Generelt"
        subtitle="Professionel koordinering af flyttegods på paller eller i container i hele verden."
        breadcrumbs={[
          { label: 'Ydelser', path: '/godstransport-i-europa' },
          { label: 'Flyttegods' }
        ]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="space-y-4 text-slate-700 leading-relaxed text-base sm:text-lg">
            <p>
              Vi koordinerer opgaven og bistår med den viden vi har akkummuleret i virksomheden. Vores service har karakter af et samarbejde, hvor afsender og modtager er involverede i større eller mindre omfang.
            </p>

            <h3 className="text-xl font-bold text-[#0f2b48] pt-2">Anbefalinger:</h3>
            <ul className="space-y-2 text-sm sm:text-base text-slate-700">
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-angle-right text-sky-600 mt-1 shrink-0" aria-hidden="true" />
                <span>at man starter i god tid afklaring, planlægning og gennemførsel tager tid - rettidig omhu.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-angle-right text-sky-600 mt-1 shrink-0" aria-hidden="true" />
                <span>at man er omhyggelig med at pakke, emballere, sikre og mærke godset. Husk, at affektionsværdi ikke kan forsikres.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-angle-right text-sky-600 mt-1 shrink-0" aria-hidden="true" />
                <span>
                  at man overvejer at tegne fragtforsikring gerne gennem sit eget forsikringsselskab eller via fastlane ApS - klik evt.{' '}
                  <a href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/instruktion-forsikring-rev-00.pdf" target="_blank" rel="noopener noreferrer" className="text-sky-700 font-semibold underline">
                    Fragtforsikring (PDF)
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-angle-right text-sky-600 mt-1 shrink-0" aria-hidden="true" />
                <span>at al løsøre skal i solide flyttekasser.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-angle-right text-sky-600 mt-1 shrink-0" aria-hidden="true" />
                <span>at møbler emballeres med bobbelplast samt evt. flamingo el. pap på udsatte steder.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-angle-right text-sky-600 mt-1 shrink-0" aria-hidden="true" />
                <span>
                  at alle colli skal mærkes med tydelig modtager detaljer - klik evt.{' '}
                  <a href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/3-3-3-rev-00-instruktion-mrking.pdf" target="_blank" rel="noopener noreferrer" className="text-sky-700 font-semibold underline">
                    Instruktion Mærkning (PDF)
                  </a>
                </span>
              </li>
            </ul>

            <h3 className="text-xl font-bold text-[#0f2b48] pt-4">Krav:</h3>
            <ul className="space-y-2 text-sm sm:text-base text-slate-700">
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-circle-exclamation text-amber-600 mt-1 shrink-0" aria-hidden="true" />
                <span>at der altid udarbejdes en detaljeret pakkeliste med dimensioner, vægt og ca. værdier. Så vi ved hvad vi kører med. Er der tale om transport af gods til eller fra 3. land (land uden for EU) skal der altid laves en udførlig pakkeliste for at godset kan udføres i afsenderlandet og for sidenhen at indfortolde godset i modtagerlandet.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-circle-exclamation text-amber-600 mt-1 shrink-0" aria-hidden="true" />
                <span>at godset må ikke indeholder farligt gods såsom beholdere under tryk, spray dåser, litium batterier, benzin/olie, kemikalier m.m.</span>
              </li>
            </ul>
          </div>

          {/* Flyttegods på paller */}
          <div className="bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200/90 space-y-4">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Flyttegods på Paller</h2>
            <p className="text-sm sm:text-base text-slate-700">
              Mellem UK og DK tilbyder vi at transportere godset fra nærmeste lovlige fortov hos afsender til nærmeste lovlige fortov hos modtager. Afsender og modtager skal selv sørge for emballering, pakning, mærkning, læsning og losning.
            </p>
            <p className="text-sm text-slate-600">
              <span className="font-bold text-slate-800">Anbefaling:</span> den god kassestørrelse er ca. 60 x 40 x 40 cm, idet denne kassestørrelse gør at en Euro pll (120 x 80 cm) udnyttes optimale - 4 i hvert lag. Kassestørrelsen passer også til UK pll (120 x 100 cm) - 5 i hvert lag.
            </p>
            <div className="text-sm text-slate-700 pt-2 space-y-2">
              <p className="font-semibold text-slate-900">Afkaringspunkter:</p>
              <p>Godset køres typisk i exporttrailer, hvilket vil sige, at der ikke er løftebagsmæk på traileren. Altså skal modtager og afsender:</p>
              <p>a) enten kunne læsse/losse godset på ladet af traileren med håndkraft</p>
              <p>b) eller vi skal hente med lille bil og løftebagsmæk</p>
              <p>c) eller godset leveres ind/afhentes fra aftalt godsterminal</p>
              <p className="text-xs text-slate-500 italic">Kan også være en kombination af ovenstående.</p>
            </div>
          </div>

          {/* Flyttegods i container */}
          <div className="bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200/90 space-y-4">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Flyttegods i Container</h2>
            <p className="text-sm sm:text-base text-slate-700">
              Vi kan sende flyttegods i container over hele verdenen.
            </p>
            <div className="text-sm text-slate-700 space-y-2">
              <p className="font-semibold text-slate-900">Afklaringspunkter:</p>
              <p>En container kan leveres på afhentnings- og leveringsadressen</p>
              <p>a) på chassis, hvilket vil sige er der ca. 130 cm til ladet</p>
              <p>b) eller med kranbil (bezima/sideloader) kan stilles på jord</p>
              <p>c) eller godset kan pakke i container på aftalt terminal</p>
              <p className="text-xs text-slate-500 italic">Kan også være en kombination af ovenstående.</p>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              Som standard har man en time til læsning og losning, hvilket normalt ikke er nok ved en flytning. Bortset fra de steder, hvor der er fritafstilling (Københavnsområdet i DK) koster det dobbelt at køre 2 gange, altså hvis en container stilles en dag og hentes den næste. Ligeledes koster det typisk 50% ekstra at levere og hente med Bezima/Sideloader.
            </p>
            <div className="bg-white p-4 rounded-xl border border-slate-200 text-xs sm:text-sm text-slate-700 space-y-2">
              <p><span className="font-bold">Eksempel 1:</span> Bruges 4 timer til læsning på chassis, betales ca. et beløb per time udover den første som er fri. evt. 400 kr. per time, alstå 1200 ekstra for 3 ventetimer.</p>
              <p><span className="font-bold">Eksempel 2:</span> Bruges 3 dage til læsning på jord, betales 2 gange kørsel (ud og hjem, ud og hjem). P.g.a. den ekstra køresel til forhøjet pris med Bezima kan det koste 3000.- til 6000.- ekstra afhængig af læssesteded.</p>
            </div>

            {/* Shippers Own Container */}
            <div className="pt-4 border-t border-slate-200 space-y-3">
              <h3 className="text-lg font-bold text-[#0f2b48]">Shippers-Own-Container</h3>
              <p className="text-sm text-slate-700 leading-relaxed">
                Normalt lejes en container af rederiet, men nogle vælger at flytte i en container de selv ejer - shippers-own-container. Sendes godset i shippers-own-container skal denne være CSC godkendt - se{' '}
                <a href="https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/3-3-6-rev-00-instruktion-container-godkendelse.pdf" target="_blank" rel="noopener noreferrer" className="text-sky-700 font-semibold underline">
                  Container Godkendelse (PDF)
                </a>
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                CSC er en forkortelse for Container Safety Convention. Alle kan se på en container, om den er CSC godkendt og om certificeringen stadig gælder, idet der skal sidde en CSC-plade på containeren. Alle nye containere har en gyldig CSC-plade, der er gældende i 5 år efter produktionen. Efter 5 år skal containeren undersøges, typisk af et containerdepot, så CSC-pladen kan opdateres, evt. efter reparation. Den kan højest opdateres i 30 måneder per gang, og kun hvis containerens stand er i orden.
              </p>
            </div>

            {/* Shipper Stow, Load and Count */}
            <div className="pt-4 border-t border-slate-200 space-y-2">
              <h3 className="text-lg font-bold text-[#0f2b48]">Shipper Stow, Load and Count</h3>
              <p className="text-sm text-slate-700 leading-relaxed">
                Begrebet er typisk gældende for containerfragt og betyder, at afsender bære ansvaret for at godset er stuvet sikkert og korrekt, læsset sikkert og korrekt samt godsmængden/antallet er korrekt. Når der er læsset forsegles containeren med et unikt segl. Et ubrudt segl er således garant for at der ikke har været nogen i containeren undervejs og at godset, burde være i den forfatning afsender efterlod det.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
